import * as React from 'react'
import { ComposedChart } from 'recharts'

import { RedGreenText } from '../../../components/RedGreenText'
import { Bar } from '../../../components/chart/Bar'
import { CartesianGrid } from '../../../components/chart/CartesianGrid'
import { ChartBox } from '../../../components/chart/ChartBox'
import { ChartWrapper } from '../../../components/chart/ChartWrapper'
import { Line } from '../../../components/chart/Line'
import { Tooltip, TooltipRow } from '../../../components/chart/Tooltip'
import { DATE_FORMAT_MMMdd, XAxis, dateAxisFormatter } from '../../../components/chart/XAxis'
import { YAxis } from '../../../components/chart/YAxis'
import { dateStrToTimestamp, shortFormatDollar } from '../../../util'
import { parseRouteInitData } from '../../../util-routing'
import { QuoteContext } from '../QuoteContext'

interface FundFlowsRow {
  date: string
  aum: number | null
  flow: number | null
}

enum FlowsChartDataKeys {
  Aum = 'aum',
  Flows = 'flow',
}

function tooltipValueFormatter(value: number, { dataKey }: { dataKey: string }) {
  const formatted = shortFormatDollar(value)
  if (dataKey === FlowsChartDataKeys.Aum) return formatted

  return (
    <RedGreenText value={value} rightContent={null}>
      {formatted}
    </RedGreenText>
  )
}

export function FundFlowsChart(props: React.HTMLProps<HTMLDivElement>) {
  const { index } = React.useContext(QuoteContext)
  const initData = React.useMemo(() => {
    const routeData = parseRouteInitData<FundFlowsRow[]>({
      elementId: `route-init-data-fundflows-${index}`,
      removeElement: false,
    })

    return routeData?.map((row) => ({ ...row, date: dateStrToTimestamp(row.date) }))
  }, [index])

  if (!initData?.length) return null

  return (
    <ChartBox {...props} label="Fund Flows">
      <ChartWrapper height={300}>
        <ComposedChart data={initData}>
          <CartesianGrid />
          <XAxis
            dataKey="date"
            scale="time"
            type="number"
            interval="equidistantPreserveStart"
            tickFormatter={(value) => dateAxisFormatter({ value, outFormat: DATE_FORMAT_MMMdd, parseAsNY: false })}
            domain={['dataMin', 'dataMax']}
          />
          <YAxis yAxisId="aumAxis" />
          <YAxis
            tickCount={null}
            yAxisId="flowAxis"
            orientation="right"
            domain={([dataMin, dataMax]) => {
              const absMax = Math.max(Math.abs(dataMin), Math.abs(dataMax)) * 1.2
              return [-absMax, absMax]
            }}
          />
          <Tooltip
            className="min-w-70"
            formatTitle={(value) => dateAxisFormatter({ value, outFormat: 'MMM dd yyyy', parseAsNY: false })}
            formatValue={tooltipValueFormatter}
          >
            <TooltipRow label="Assets Under Management" dataKey={FlowsChartDataKeys.Aum} />
            <TooltipRow label="Flow" dataKey={FlowsChartDataKeys.Flows} />
          </Tooltip>

          <Line dataKey={FlowsChartDataKeys.Aum} yAxisId="aumAxis" />
          <Bar<number>
            yAxisId="flowAxis"
            dataKey={FlowsChartDataKeys.Flows}
            radius={[1, 1, 0, 0]}
            color={(value) => (value == null ? 'neutral' : value > 0 ? 'green' : 'red')}
          />
        </ComposedChart>
      </ChartWrapper>
    </ChartBox>
  )
}
